function textValue(value) {
  return String(value ?? '').trim();
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

// Everything a leader can actually edit on a plan. Ids and timestamps are left out so a plan that
// came back from a save compares equal to the draft it was saved from.
export function strategyPlanSignature(plan = {}) {
  return JSON.stringify({
    bossNo: numberValue(plan.bossNo),
    phaseNo: numberValue(plan.phaseNo),
    targetHpEok: numberValue(plan.targetHpEok),
    note: textValue(plan.note),
    items: (Array.isArray(plan.items) ? plan.items : []).map((item) => ({
      practiceId: textValue(item.practiceId),
      memberOpenid: textValue(item.memberOpenid),
      nickname: textValue(item.nickname),
      bossNo: numberValue(item.bossNo),
      slotNo: numberValue(item.slotNo, 1),
      title: textValue(item.title),
      damageEok: numberValue(item.damageEok),
      deck: Array.isArray(item.deck) ? item.deck : [],
      note: textValue(item.note),
    })),
  });
}

// The plan board keeps drafts for all 15 boss×phase plans but saves one phase at a time, while the
// stored plans reload on every save and on every silent raid refresh (bridge sync). Replacing every
// draft with the stored plan on each reload — what the board used to do — throws away edits the
// leader has not saved yet, including whole phases filled by a recommendation.
//
// So: a draft that still matches the stored plan it was built from follows the new stored plan, and
// a draft the leader has edited since then stays. `conflicts` are keys edited locally whose stored
// plan also moved — someone else saved that plan — so the board can offer to reload instead of
// silently overwriting. (A phase this board just saved is adopted directly by the caller, so it
// never reaches here as a conflict.)
export function mergeStrategyDrafts({
  drafts = {},
  incoming = {},
  savedSignatures = new Map(),
  signature = strategyPlanSignature,
} = {}) {
  const nextDrafts = {};
  const nextSignatures = new Map();
  const conflicts = [];

  for (const [key, incomingPlan] of Object.entries(incoming)) {
    const incomingSignature = signature(incomingPlan);
    nextSignatures.set(key, incomingSignature);

    const draft = drafts[key];
    const previousSignature = savedSignatures.get(key);
    const edited = Boolean(draft) && previousSignature !== undefined && signature(draft) !== previousSignature;

    if (!edited) {
      nextDrafts[key] = incomingPlan;
      continue;
    }

    nextDrafts[key] = draft;
    if (incomingSignature !== previousSignature) conflicts.push(key);
  }

  return { drafts: nextDrafts, savedSignatures: nextSignatures, conflicts };
}

// Emptying a phase keeps the slots a real attack already matched: dropping those would only turn
// finished attacks into "계획 외 실전" warnings, while the point of clearing is to redo what is
// still *planned*. `reconcile` is passed in (reconcileRaidStrategy) to keep this file dependency-free.
export function clearUnmatchedPlanItems({ plan, actualRecords = [], reconcile } = {}) {
  const items = Array.isArray(plan?.items) ? plan.items : [];
  if (!items.length || typeof reconcile !== 'function') return { plan, cleared: 0 };

  const matched = new Set(reconcile({ plans: [plan], actualRecords })
    .matchedPlans.map((entry) => entry.planIndex));
  if (items.length === matched.size) return { plan, cleared: 0 };

  return {
    plan: { ...plan, items: items.filter((_, index) => matched.has(index)) },
    cleared: items.length - matched.size,
  };
}
